import React, { useContext, useState } from 'react';
import { Button, Grid, Segment, Form, Divider } from 'semantic-ui-react';
import { DateRangePowerSelector, ModulesFilter, NumberRangePowerSelector, PowerSelector, Person, USER_PERMISSIONS } from 'dyl-components';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BulkActionsContext } from 'shared/context/BulkActionsProvider';
import { KEY_ACCOUNT_CO_OWNER, KEY_ACCOUNT_NAME, KEY_OPPORTUNITY_OUTCOME, KEY_ACCOUNT_OWNER, KEY_CONTACT_CO_OWNER, KEY_CONTACT_NAME, KEY_CONTACT_OWNER, KEY_CREATED, KEY_CREATED_BY, KEY_EXPIRES_ON, KEY_LAST_MODIFIED, KEY_LAST_MODIFIED_BY, KEY_NUMBER_PRODUCTS, KEY_OPPORTUNITY_NAME, KEY_PRODUCTS, KEY_PROPOSAL_INVOICE_CREATED, KEY_STATUS, KEY_SUBTOTAL, KEY_ACCOUNT_STATUS } from 'shared/constants/QUOTES_ORDERS_COLUMNS';
import { StringUtils } from 'utils';
import RestrictedView from 'shared/RestrictedView';

const COLUMN_ROWS = [
    [
        { value: KEY_CREATED, label: "Created" },
        { value: KEY_NUMBER_PRODUCTS, label: "# Products" },
        { value: KEY_ACCOUNT_NAME, label: "Account Name" },
    ],
    [
        { value: KEY_CREATED_BY, label: "Created By" },
        { value: KEY_PRODUCTS, label: "Products" },
        { value: KEY_OPPORTUNITY_NAME, label: "Opportunity Name" },
    ],
    [
        { value: KEY_LAST_MODIFIED, label: "Last Modified" },
        { value: KEY_CONTACT_OWNER, label: "Contact Owned By" },
        { value: KEY_OPPORTUNITY_OUTCOME, label: "Opportunity Outcome" },
    ],
    [
        { value: KEY_LAST_MODIFIED_BY, label: "Last Modified By" },
        { value: KEY_CONTACT_CO_OWNER, label: "Contact Co-Owned By" },
        { value: KEY_ACCOUNT_STATUS, label: "Account Status" }
    ],
    [
        { value: KEY_PROPOSAL_INVOICE_CREATED, label: "Proposal/Invoice Created" },
        { value: KEY_ACCOUNT_OWNER, label: "Account Owned By" },
        { value: KEY_SUBTOTAL, label: "Subtotal" },
    ],
    [
        { value: KEY_EXPIRES_ON, label: "Expires On" },
        { value: KEY_ACCOUNT_CO_OWNER, label: "Account Co-Owned By" },
        { value: KEY_STATUS, label: "Status" },
    ],
    [
        { value: KEY_EXPIRES_ON, label: "ID", isForcedChecked: true },
        { value: KEY_CONTACT_NAME, label: "Contact Name" },
    ]
];

const QUOTES_AND_ORDERS_FILTER_PARAMS = ["contact_owner", "contact_coowner", "created_by", "account_coowner", "account_owner", "account_status", "account_name", "contact_name", "opportunity_name", "opportunity_outcome"]


const QuotesAndOrdersToolbar = ({ isColumnHidden, onToggleColumn }) => {
    const [selectedQuotesAndOrders, , areQuotesAndOrdersInAllPagesSelected] = useContext(BulkActionsContext);

    const hasSelectedQuotesOrOrders = selectedQuotesAndOrders.length || areQuotesAndOrdersInAllPagesSelected;

    const [, setIsBulkActionsModalOpen] = useState(false);

    const onOpenBulkActionsModal = () => {
        setIsBulkActionsModalOpen(true);
    }

    const [params, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();


    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            filters: {
                created_by: (params.get("created_by")?.split(",") || []).map(id => Number(id)),
                account_coowner: (params.get("account_coowner")?.split(",") || []).map(id => Number(id)),
                account_owner: (params.get("account_owner")?.split(",") || []).map(id => Number(id)),
                account_status: (params.get("account_status")?.split(",") || []).map(id => Number(id)),
                account_name: (params.get("account_name")?.split(",") || []).map(id => Number(id)),
                contact_owner: (params.get("contact_owner")?.split(",") || []).map(id => Number(id)),
                contact_coowner: (params.get("contact_coowner")?.split(",") || []).map(id => Number(id)),
                contact_name: (params.get("contact_name")?.split(",") || []).map(id => Number(id)),
                opportunity_name: (params.get("opportunity_name")?.split(",") || []).map(id => Number(id)),
                opportunity_outcome: (params.get("opportunity_outcome")?.split(",") || []).map(id => Number(id)),
            },
        }
    });

    console.log(methods?.getValues());
    

    const onSubmit = methods.handleSubmit((data) => {
        const filters = { ...data?.filters };
        const existingFiltersObj = Object.fromEntries(params.entries());
        const mergedFilters = { ...existingFiltersObj, ...filters };

        const apiFilters = new URLSearchParams();
        Object.entries(mergedFilters).forEach(([key, value]) => {
            if (Array.isArray(value) && value?.length) {
                apiFilters.set(key, value?.join?.(","));
            } else if (
                key === "created_start_at" ||
                key === "created_end_at" ||
                (typeof value === "string"
                    ? value.trim().length > 0
                    : value?.length > 0)
            ) {
                apiFilters.set(key, value);
            }
        });
        apiFilters.set("page", "1");
        setSearchParams(apiFilters);
    });

    const onClearFilter = () => {
        methods.reset()

        const columns = params.get("columns");

        const queryParams = new URLSearchParams();

        if (columns) queryParams.set("columns", columns);
        queryParams.set("page", "1");

        const queryString = queryParams.toString();
        const url = queryString
            ? `${location.pathname}?${queryString}`
            : location.pathname;

        navigate(url);
    };

    const unassignedOption = { value: 0, text: "Unassigned", key: 0 };
    const powerSelectors = useSelector(state => (state.quotes_and_orders.quotesAndOrdersPowerSelectors));
    console.log("|+|power", powerSelectors)
    const accountOwnerOptions = [...(powerSelectors?.ownersAccount?.some?.(owner => !owner?.account_id)
        ? [unassignedOption]
        : []), ...(powerSelectors?.ownersAccount?.filter?.(owner => owner?.account_id).map(owner => ({
            value: owner.account_id,
            text: owner.username,
            key: owner.account_id,
            content: (<><Person username={owner.username} email={owner.email} isInactiveUser={!owner.active} /></>)
        })) || [])];
    const accountCoOwnerOptions = [
        ...(powerSelectors?.co_ownersAccount?.some?.(co_owner => !co_owner?.account_id)
            ? [unassignedOption]
            : [])
        , ...(powerSelectors?.co_ownersAccount?.filter?.(co_owner => co_owner?.account_id).map(co_owner => ({
            value: co_owner.account_id,
            text: co_owner.username,
            key: co_owner.account_id,
            content: (<><Person username={co_owner.username} email={co_owner.email} isInactiveUser={!co_owner.active} /></>)
        })) || [])];
    const accountNames = [...(powerSelectors?.accounts?.map(name => ({
        value: name.id,
        text: name.name,
        key: name.id,
    })) || [])];
    const accountStatus = [...(powerSelectors?.accountStatus?.map(status => ({
        value: status,
        text: StringUtils.capitalize(status),
        key: status,
    })) || [])];
    const contactNames = [...(powerSelectors?.contacts?.map(name => ({
        value: name.id,
        text: name.name,
        key: name.id,
    })) || [])];
    
    const contactOwnerOptions = [
        ...(
            powerSelectors?.owners?.some?.(owner => !owner?.person_id)
                ? [unassignedOption]
                : []
        ),
        ...(powerSelectors?.owners?.filter?.(owner => owner?.person_id).map(owner => ({
            value: owner.person_id,
            text: owner.username,
            key: owner.person_id,
            content: (<><Person username={owner.username} email={owner.email} isInactiveUser={!owner.active} /></>)
        })) || [])];
    const contactCoOwnerOptions = [
        ...(
            powerSelectors?.co_owners?.some?.(co_owner => !co_owner?.person_id)
                ? [unassignedOption]
                : []
        ),
        ...(powerSelectors?.co_owners?.filter?.(co_owner => co_owner?.person_id).map(co_owner => ({
            value: co_owner.person_id,
            text: co_owner.username,
            key: co_owner.person_id,
            content: (<><Person username={co_owner.username} email={co_owner.email} isInactiveUser={!co_owner.active} /></>)
        })) || [])];
    const opportunityName = [...(powerSelectors?.opportunities?.map(opportunity => ({
        value: opportunity.opportunity_id,
        text: opportunity.opportunity_name,
        key: opportunity.opportunity_id,
    })) || [])];
    const opportunityOutcome = [...(powerSelectors?.opportunity_outcome?.map(outcome => ({
        value: outcome,
        text: StringUtils.capitalize(outcome).replace(/\(/g, " ("),
        key: outcome,
    })) || [])];


    return (
        <Grid className='QuotesAndOrdersToolbar'>
            <Grid.Row>
                <Grid.Column>
                    <FormProvider {...methods}>
                        <Form onSubmit={onSubmit}>
                            <Segment.Group>
                                <Segment>
                                    <DateRangePowerSelector
                                        label={"Created"}
                                        name={"created"}
                                    />
                                    <PowerSelector
                                        label={"Created By"}
                                        multiple
                                        name={"created_by"}
                                        options={[]}
                                    />
                                    <DateRangePowerSelector
                                        label={"Last Modified"}
                                        name={"last_modified"}
                                    />
                                    <PowerSelector
                                        label={"Last Modified By"}
                                        multiple
                                        name={"last_modified_by"}
                                        options={[]}
                                    />
                                    <DateRangePowerSelector
                                        label={"Proposal/Invoice Created"}
                                        name={"proposal_invoice_created"}
                                    />
                                    <DateRangePowerSelector
                                        label={"Expires On"}
                                        name={"expires_on"}
                                    />
                                    <NumberRangePowerSelector
                                        label={"# Products"}
                                        name={"number_products"}
                                        min={1}
                                    />
                                    <PowerSelector
                                        label={"Products"}
                                        multiple
                                        name={"products"}
                                        options={[]}
                                    />
                                    <PowerSelector
                                        label={"Contact Owned By"}
                                        multiple
                                        name={"contact_owner"}
                                        options={contactOwnerOptions}
                                        className="InvoicesToolbar__Options--scroll"
                                    />
                                    <PowerSelector
                                        label={"Contact Co-Owned By"}
                                        multiple
                                        name={"contact_coowner"}
                                        options={contactCoOwnerOptions}
                                        className="InvoicesToolbar__Options--scroll"

                                    />
                                    <PowerSelector
                                        label={"Account Owned By"}
                                        multiple
                                        name={"account_owner"}
                                        options={accountOwnerOptions}
                                        className="InvoicesToolbar__Options--scroll"
                                    />
                                    <PowerSelector
                                        label={"Account Co-Owned By"}
                                        multiple
                                        name={"account_coowner"}
                                        options={accountCoOwnerOptions}
                                        className="InvoicesToolbar__Options--scroll"
                                    />
                                    <PowerSelector
                                        label={"Contact Name"}
                                        multiple
                                        name={"contact_name"}
                                        options={contactNames}
                                        className="InvoicesToolbar__Options--scroll"
                                    />
                                    <PowerSelector
                                        label={"Account Name"}
                                        multiple
                                        name={"account_name"}
                                        options={accountNames}
                                        className="InvoicesToolbar__Options--scroll"
                                    />
                                    <PowerSelector
                                        label={"Account Status"}
                                        multiple
                                        name={"account_status"}
                                        options={accountStatus}
                                        className="InvoicesToolbar__Options--scroll"
                                    />
                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.OPPORTUNITY_VIEW}>
                                        <PowerSelector
                                            label={"Opportunity Name"}
                                            multiple
                                            name={"opportunity_name"}
                                            options={opportunityName}
                                            className="InvoicesToolbar__Options--scroll"
                                        />
                                    </RestrictedView>
                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.OPPORTUNITY_VIEW}>
                                        <PowerSelector
                                            label={"Opportunity Outcome"}
                                            multiple
                                            name={"opportunity_outcome"}
                                            options={opportunityOutcome}
                                            className="InvoicesToolbar__Options--scroll"
                                        />
                                    </RestrictedView>
                                    <NumberRangePowerSelector
                                        label={"Subtotal"}
                                        name={"subtotal"}
                                        min={1}
                                        isCurrency
                                    />
                                    <PowerSelector
                                        label={"Status"}
                                        multiple
                                        name={"status"}
                                        options={[]}
                                    />
                                </Segment>
                                <Segment textAlign="right">
                                    <Button
                                        onClick={onClearFilter}
                                        color="primary"
                                        basic
                                        disabled={
                                            !QUOTES_AND_ORDERS_FILTER_PARAMS.some(
                                                (param) => params.get(param)
                                            )
                                        }
                                    >
                                        Clear Filters
                                    </Button>
                                    <Divider vertical />
                                    <Button
                                        disabled={!methods.formState.isValid || !methods.formState.isDirty}
                                        color="primary"
                                        type="submit"
                                    >
                                        Apply
                                    </Button>
                                </Segment>
                            </Segment.Group>
                        </Form>
                    </FormProvider>
                </Grid.Column>
            </Grid.Row>
            <Grid.Row columns='equal' verticalAlign='middle'>
                <Grid.Column style={{ marginLeft: "-2.75em" }} width={1}>
                    <ModulesFilter
                        hasSearchBar={false}
                        columnRows={COLUMN_ROWS}
                        columnCount={3}
                        isColumnHidden={isColumnHidden}
                        onToggleColumn={onToggleColumn}
                    />
                </Grid.Column>
                <Grid.Column />
                <Grid.Column width={4} floated='right'>
                    <Button floated='right' className='QuotesAndOrdersToolbar__QuotesAndOrdersButton' disabled={true || !hasSelectedQuotesOrOrders} onClick={onOpenBulkActionsModal}>
                        Bulk Action
                    </Button>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    )
}

export default QuotesAndOrdersToolbar;
